/**
 * Ilustrações do produto (SVG inline, sem dependências). Usam os tokens do tema via CSS
 * variables (com fallback para a paleta v2), então acompanham o claro e o escuro:
 * fundo violeta suave, "papel" e telas na superfície, objetos em tinta, realces em lima.
 */
import type { SVGProps } from 'react';

const P = 'var(--color-primary, #6B4EFF)';
/** fundo (bolha atrás da cena) */
const PS = 'var(--illo-blob, #EEEAFF)';
/** realce: lima (sobre tinta ou violeta) */
const PB = 'var(--color-accent, #C8F169)';
/** objetos escuros (máquinas, celulares) */
const D = 'var(--illo-ink, #16161E)';
const G = 'var(--color-gold, #FFB81C)';
/** contornos e linhas de texto fictício */
const L = 'var(--illo-line, #E3E3EA)';
/** papel secundário (cartões dentro da cena) */
const BG = 'var(--illo-paper-2, #F4F4F7)';
const R = 'var(--color-danger-solid, #C8283A)';
/** papel e telas (vira superfície escura no tema escuro) */
const PAPER = 'var(--illo-paper, #FFFFFF)';
/** marcas brancas sobre cores cheias (check, texto em botão) */
const W = '#FFFFFF';
const FONT = "'Geist Variable', Geist, system-ui, Arial, sans-serif";

type Props = SVGProps<SVGSVGElement>;
const base = (props: Props) => ({ viewBox: '0 0 240 180', fill: 'none', role: 'img' as const, 'aria-hidden': true, className: 'h-auto w-full', ...props });

export function IllustrationForklift(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <rect x="18" y="150" width="204" height="4" rx="2" fill={L} />
      {/* caixas */}
      <rect x="128" y="96" width="44" height="38" rx="4" fill={G} />
      <rect x="146" y="96" width="8" height="38" fill="#E7A40F" />
      <rect x="134" y="60" width="36" height="34" rx="4" fill="#FFCD5A" />
      <rect x="148" y="60" width="8" height="34" fill={G} />
      {/* garfo e mastro */}
      <rect x="116" y="30" width="6" height="118" rx="2" fill={D} />
      <rect x="122" y="134" width="56" height="5" rx="2" fill={D} />
      {/* corpo */}
      <path d="M44 104c0-8 6-14 14-14h30l16 22v26H44z" fill={P} />
      <path d="M62 66h26l16 24H62z" fill={PB} opacity=".35" />
      <path d="M62 66h24l18 24" stroke={D} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="40" y="96" width="10" height="30" rx="3" fill={D} />
      <rect x="84" y="112" width="30" height="8" rx="3" fill={D} opacity=".85" />
      {/* rodas */}
      <circle cx="62" cy="142" r="12" fill={D} />
      <circle cx="62" cy="142" r="4.5" fill={W} />
      <circle cx="100" cy="142" r="12" fill={D} />
      <circle cx="100" cy="142" r="4.5" fill={W} />
      <circle cx="200" cy="40" r="6" fill={G} />
      <circle cx="34" cy="48" r="4" fill={PB} />
    </svg>
  );
}

export function IllustrationCashRegister(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <rect x="18" y="152" width="204" height="4" rx="2" fill={L} />
      {/* recibo */}
      <path d="M142 22h40v58l-5-4-5 4-5-4-5 4-5-4-5 4-5-4-5 4z" fill={PAPER} stroke={L} strokeWidth="2" />
      <rect x="150" y="32" width="24" height="3" rx="1.5" fill={L} />
      <rect x="150" y="41" width="18" height="3" rx="1.5" fill={L} />
      <rect x="150" y="50" width="22" height="3" rx="1.5" fill={P} />
      {/* máquina */}
      <rect x="54" y="44" width="80" height="44" rx="8" fill={D} />
      <rect x="62" y="52" width="64" height="22" rx="4" fill={PB} />
      <text x="94" y="68" textAnchor="middle" fontFamily={FONT} fontSize="12" fontWeight="700" fill={D}>R$ 100,00</text>
      <path d="M40 96c0-4 4-8 8-8h144c4 0 8 4 8 8v30H40z" fill={P} />
      {[0, 1, 2, 3].map((c) =>
        [0, 1].map((r) => <rect key={`${c}-${r}`} x={58 + c * 22} y={98 + r * 12} width="16" height="7" rx="2" fill={W} opacity=".9" />),
      )}
      <rect x="150" y="98" width="36" height="19" rx="3" fill={PB} />
      <rect x="32" y="126" width="176" height="24" rx="6" fill={D} />
      <rect x="104" y="134" width="32" height="5" rx="2.5" fill={G} />
      <circle cx="34" cy="40" r="5" fill={G} />
    </svg>
  );
}

export function IllustrationStorefront(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      {/* monitor */}
      <rect x="34" y="28" width="172" height="112" rx="10" fill={D} />
      <rect x="42" y="36" width="156" height="96" rx="5" fill={PAPER} />
      <rect x="104" y="140" width="32" height="14" fill={D} />
      <rect x="84" y="152" width="72" height="6" rx="3" fill={D} />
      {/* toldo */}
      <path d="M42 36h156v18H42z" fill={P} />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <path key={i} d={`M${42 + i * 26} 54q13 12 26 0`} fill={i % 2 ? P : PB} />
      ))}
      {/* produtos */}
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x={52 + i * 48} y="76" width="40" height="48" rx="5" fill={BG} stroke={L} />
          <rect x={58 + i * 48} y="82" width="28" height="22" rx="3" fill={i === 1 ? G : PS} />
          <rect x={58 + i * 48} y="108" width="20" height="4" rx="2" fill={L} />
          <rect x={58 + i * 48} y="115" width="14" height="4" rx="2" fill={P} />
        </g>
      ))}
      <circle cx="210" cy="36" r="6" fill={G} />
    </svg>
  );
}

export function IllustrationNotebook(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      {/* caderneta riscada */}
      <g transform="rotate(-8 80 96)">
        <rect x="36" y="42" width="84" height="104" rx="6" fill="#FFF3D6" stroke="#E7C680" strokeWidth="2" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <rect key={i} x="48" y={60 + i * 12} width={i % 2 ? 44 : 58} height="3" rx="1.5" fill="#D9B870" />
        ))}
        <rect x="36" y="42" width="10" height="104" fill="#E7C680" />
        <path d="M44 56l64 80" stroke={R} strokeWidth="5" strokeLinecap="round" />
      </g>
      {/* celular com conta organizada */}
      <rect x="128" y="30" width="72" height="124" rx="12" fill={D} />
      <rect x="134" y="40" width="60" height="104" rx="6" fill={PAPER} />
      <circle cx="150" cy="56" r="8" fill={P} />
      <rect x="162" y="51" width="26" height="4" rx="2" fill={L} />
      <rect x="162" y="59" width="18" height="4" rx="2" fill={L} />
      <rect x="140" y="74" width="48" height="16" rx="4" fill={PS} />
      <text x="164" y="86" textAnchor="middle" fontFamily={FONT} fontSize="9" fontWeight="700" fill={P}>Deve R$ 90</text>
      <rect x="140" y="96" width="48" height="4" rx="2" fill={L} />
      <rect x="140" y="104" width="36" height="4" rx="2" fill={L} />
      <rect x="140" y="120" width="48" height="14" rx="7" fill={P} />
      <circle cx="206" cy="126" r="14" fill={PB} />
      <path d="M199 126l5 5 9-10" stroke={D} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IllustrationPix(props: Props) {
  const qr = [
    [1, 1, 1, 0, 1, 0, 1, 1, 1],
    [1, 0, 1, 0, 0, 1, 1, 0, 1],
    [1, 1, 1, 0, 1, 0, 1, 1, 1],
    [0, 0, 0, 1, 1, 0, 0, 0, 0],
    [1, 0, 1, 1, 0, 1, 1, 0, 1],
    [0, 1, 0, 0, 1, 1, 0, 1, 0],
    [1, 1, 1, 0, 0, 1, 1, 0, 1],
    [1, 0, 1, 1, 0, 0, 1, 1, 0],
    [1, 1, 1, 0, 1, 1, 0, 1, 1],
  ];
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <rect x="78" y="18" width="84" height="148" rx="14" fill={D} />
      <rect x="85" y="30" width="70" height="124" rx="7" fill={PAPER} />
      <g transform="translate(93 46)">
        {qr.map((row, y) => row.map((v, x) => (v ? <rect key={`${x}-${y}`} x={x * 6} y={y * 6} width="6" height="6" fill={D} /> : null)))}
      </g>
      <rect x="93" y="110" width="54" height="5" rx="2.5" fill={L} />
      <rect x="93" y="122" width="54" height="16" rx="8" fill={P} />
      <text x="120" y="133.5" textAnchor="middle" fontFamily={FONT} fontSize="8" fontWeight="700" fill={W}>Pago ✓</text>
      {/* losango */}
      <g transform="translate(182 58) rotate(45)">
        <rect x="-16" y="-16" width="32" height="32" rx="7" fill={PB} />
        <rect x="-8" y="-8" width="16" height="16" rx="3" fill={D} />
      </g>
      <circle cx="44" cy="124" r="18" fill={G} />
      <text x="44" y="129" textAnchor="middle" fontFamily={FONT} fontSize="14" fontWeight="800" fill={D}>R$</text>
    </svg>
  );
}

export function IllustrationContacts(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <rect x="64" y="26" width="112" height="132" rx="10" fill={PAPER} stroke={L} strokeWidth="2" />
      <rect x="56" y="40" width="12" height="10" rx="3" fill={P} />
      <rect x="56" y="70" width="12" height="10" rx="3" fill={P} />
      <rect x="56" y="100" width="12" height="10" rx="3" fill={P} />
      <rect x="56" y="130" width="12" height="10" rx="3" fill={P} />
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <circle cx="92" cy={52 + i * 36} r="11" fill={i === 1 ? G : PS} stroke={P} strokeWidth="2" />
          <rect x="110" y={46 + i * 36} width="48" height="5" rx="2.5" fill={D} opacity=".8" />
          <rect x="110" y={55 + i * 36} width="32" height="4" rx="2" fill={L} />
        </g>
      ))}
      <circle cx="176" cy="30" r="16" fill={P} />
      <path d="M176 22v16M168 30h16" stroke={W} strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}

export function IllustrationChart(props: Props) {
  const bars = [34, 52, 40, 70, 58, 86, 74];
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <rect x="30" y="28" width="180" height="124" rx="12" fill={PAPER} stroke={L} strokeWidth="2" />
      <rect x="44" y="40" width="54" height="6" rx="3" fill={D} opacity=".8" />
      <rect x="44" y="51" width="34" height="5" rx="2.5" fill={L} />
      {bars.map((h, i) => (
        <rect key={i} x={52 + i * 22} y={136 - h} width="14" height={h} rx="4" fill={P} opacity={i === 5 ? 1 : 0.22} />
      ))}
      <path d="M59 108l22-14 22 8 22-26 22 10 22-30 22 6" stroke={G} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="191" cy="62" r="5" fill={G} />
    </svg>
  );
}

export function IllustrationCoupons(props: Props) {
  const ticket = (x: number, y: number, rot: number, color: string, text: string, ink: string) => (
    <g transform={`rotate(${rot} ${x + 60} ${y + 26})`}>
      <path
        d={`M${x} ${y}h120v18a8 8 0 0 0 0 16v18H${x}V${y + 34}a8 8 0 0 0 0-16z`}
        fill={color}
      />
      <path d={`M${x + 84} ${y + 6}v40`} stroke={ink} strokeWidth="2" strokeDasharray="4 4" opacity=".5" />
      <text x={x + 42} y={y + 34} textAnchor="middle" fontFamily={FONT} fontSize="20" fontWeight="800" fill={ink}>
        {text}
      </text>
    </g>
  );
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      {ticket(40, 44, -10, PB, '10%', '#0E0E14')}
      {ticket(70, 92, 6, P, '-20%', W)}
      <circle cx="198" cy="40" r="6" fill={R} />
      <circle cx="36" cy="138" r="5" fill={PB} />
    </svg>
  );
}

export function IllustrationEmptyBox(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <path d="M60 84l60-24 60 24v54l-60 24-60-24z" fill="#F2C57C" />
      <path d="M120 108v54l60-24V84z" fill="#E0AE5E" />
      <path d="M60 84l60 24 60-24-60-24z" fill="#FAD9A1" />
      <path d="M60 84l-16 22 60 24 16-22zM180 84l16 22-60 24-16-22z" fill="#F7CF8B" />
      <circle cx="120" cy="40" r="4" fill={P} />
      <circle cx="96" cy="30" r="3" fill={G} />
      <circle cx="146" cy="28" r="3.5" fill={PB} />
    </svg>
  );
}

export function IllustrationSuccess(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <circle cx="120" cy="90" r="48" fill={P} />
      <path d="M98 91l15 15 30-32" stroke={W} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
      {[
        [48, 50, G],
        [190, 46, PB],
        [60, 140, PB],
        [184, 136, G],
        [36, 96, R],
        [204, 92, P],
      ].map(([x, y, c], i) => (
        <rect key={i} x={Number(x)} y={Number(y)} width="9" height="9" rx="2" fill={String(c)} transform={`rotate(${i * 25} ${x} ${y})`} />
      ))}
    </svg>
  );
}

export function IllustrationOrders(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <rect x="62" y="30" width="96" height="128" rx="10" fill={PAPER} stroke={L} strokeWidth="2" />
      <rect x="88" y="22" width="44" height="16" rx="6" fill={D} />
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <rect x="76" y={54 + i * 24} width="12" height="12" rx="3" fill={i < 2 ? P : L} />
          {i < 2 ? <path d={`M79 ${60 + i * 24}l2.5 2.5 4.5-5`} stroke={W} strokeWidth="2" strokeLinecap="round" /> : null}
          <rect x="96" y={56 + i * 24} width={i % 2 ? 36 : 48} height="4" rx="2" fill={D} opacity=".7" />
          <rect x="96" y={63 + i * 24} width="24" height="3" rx="1.5" fill={L} />
        </g>
      ))}
      <path d="M156 92h40l-6 52h-28z" fill={G} />
      <path d="M166 92v-8a10 10 0 0 1 20 0v8" stroke={D} strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

export function IllustrationTeam(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <circle cx="92" cy="70" r="22" fill={G} />
      <path d="M56 142c0-22 16-38 36-38s36 16 36 38z" fill={P} />
      <circle cx="152" cy="78" r="18" fill={PB} />
      <path d="M122 146c0-18 13-32 30-32s30 14 30 32z" fill={D} />
      <circle cx="190" cy="44" r="12" fill={PAPER} stroke={P} strokeWidth="3" />
      <path d="M184 44l4 4 8-8" stroke={P} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function IllustrationWallet(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <rect x="70" y="36" width="92" height="56" rx="6" fill={PB} transform="rotate(-10 116 64)" />
      <rect x="58" y="60" width="124" height="86" rx="14" fill={P} />
      <rect x="140" y="88" width="50" height="30" rx="8" fill={D} />
      <circle cx="156" cy="103" r="6" fill={G} />
      <rect x="72" y="74" width="46" height="5" rx="2.5" fill={W} opacity=".6" />
      <circle cx="46" cy="52" r="12" fill={G} />
      <text x="46" y="56.5" textAnchor="middle" fontFamily={FONT} fontSize="12" fontWeight="800" fill={D}>$</text>
    </svg>
  );
}

export function IllustrationOffline(props: Props) {
  return (
    <svg {...base(props)}>
      <ellipse cx="120" cy="92" rx="104" ry="78" fill={PS} />
      <path d="M76 124a26 26 0 0 1 2-52 36 36 0 0 1 68-6 30 30 0 0 1 20 58z" fill={PAPER} stroke={L} strokeWidth="2" />
      <path d="M84 60l80 80" stroke={R} strokeWidth="7" strokeLinecap="round" />
      <circle cx="198" cy="50" r="6" fill={G} />
    </svg>
  );
}

/**
 * Marca da plataforma (BRAND §6): "dezza" em Geist 700, minúscula, espaçamento -0,04 em, e o
 * ponto na cor de acento do contexto.
 * - `dark` (padrão): texto em tinta sobre superfícies do tema; ponto violeta no claro e lima no escuro.
 * - `light`: para superfícies sempre escuras (menu lateral, barra do celular); texto branco e ponto lima.
 */
export function Logo({ className, tone = 'dark', name = 'dezza' }: { className?: string; tone?: 'dark' | 'light'; name?: string }) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        fontFamily: "var(--font-sans, 'Geist Variable', Geist, system-ui, sans-serif)",
        fontWeight: 700,
        letterSpacing: '-0.04em',
      }}
    >
      <span style={{ color: tone === 'light' ? '#FFFFFF' : 'var(--color-ink, #0E0E14)' }}>{name.toLowerCase()}</span>
      <span style={{ color: tone === 'light' ? '#C8F169' : 'var(--color-brand, #6B4EFF)' }}>.</span>
    </span>
  );
}
